import { toUnityFixed } from '../../../../helpers/unityHelper';
import { changeIntensity, updateDisplacementMapChangedWithCycle, removeDisplacement, selectObjectBeforeUndoRedo, disableGameObject, enableGameObject } from '../ProductBuilderComponents/commonHelper';
import { TILING_TYPE } from '../../../../config/constant/unityConstants';
import { convertActiveUnitToCM } from '../../../../helpers/curatorActions';
import { undoRedo as undoRedoObject } from './UndoRedo';
import { UnityEvent, waitForEventOnce } from '../../../../helpers/unityPubSub';
import { selectSelectedObjectName } from '../../../../redux/slicers/admin/curatorUnityObjectSlicer';
import { getAppState } from '../../../../redux/store';

export class UndoRedoWall {
  constructor({ undoRedoInstance }) {
    this.undoRedo = undoRedoInstance;
    this.pending = new Map();
    this.createAction = this.undoRedo.createAction;
    this._init();
  }

  _init() {
    // Single property change
    this._pushSingleProp = this.createAction(
      ({ prevValue, nextValue, unityProp, updateCallback }) => {
        if (prevValue === nextValue) return false;
        const selectedObjectName = selectSelectedObjectName(getAppState());  
        return {
          __ACTION__: 'WALL_PROP_CHANGE',
          undo: async () => {
            await selectObjectBeforeUndoRedo(selectedObjectName) 
            await this._applyUnityChange(unityProp, prevValue);
            updateCallback?.(unityProp, prevValue);
            updateDisplacementMapChangedWithCycle();
          },
          redo: async () => {
            const res = await selectObjectBeforeUndoRedo(selectedObjectName)
            await this._applyUnityChange(unityProp, nextValue);
            updateCallback?.(unityProp, nextValue);
            updateDisplacementMapChangedWithCycle();
          },
        };
      },
      { pause: true, name: 'WALL_PROP_CHANGE' }
    );

    // Multiple properties change (for locked width/height)
    this._pushMultiProp = this.createAction(
      ({ changes, updateCallback }) => {
        const hasChange = changes.some((c) => {
          if (typeof c.prevValue === 'object') {
            return c.prevValue.width !== c.nextValue.width || c.prevValue.height !== c.nextValue.height;
          }
          return c.prevValue !== c.nextValue;
        });
        if (!hasChange) return false;

        return {
          __ACTION__: 'WALL_MULTI_PROP_CHANGE',
          undo: async () => {
            for (const c of changes.slice().reverse()) {
              await this._applyUnityChange(c.unityProp, c.prevValue);
              updateCallback?.(c.unityProp, c.prevValue);
            }
            updateDisplacementMapChangedWithCycle();
          },
          redo: async () => {
            for (const c of changes) {
              await this._applyUnityChange(c.unityProp, c.nextValue);
              updateCallback?.(c.unityProp, c.nextValue);
            }
            updateDisplacementMapChangedWithCycle();
          },
        };
      },
      { pause: true, name: 'WALL_MULTI_PROP_CHANGE' }
    );

    // Pattern selection change
    this._pushPatternChange = this.createAction(
      ({ prevPattern, nextPattern, applyTextureCallback, getDataCallback, updateUICallback, wasListOpen, prevValue }) => {
        // Compare pattern IDs
        const prevId = prevPattern?.id;
        const nextId = nextPattern?.id;
        
        if (prevId === nextId) return false;
        const selectedObjectName = selectSelectedObjectName(getAppState());  

        return {
          __ACTION__: 'WALL_PATTERN_CHANGE',
          undo: async () => {
            if (prevPattern?.id) {
              await selectObjectBeforeUndoRedo(selectedObjectName) 
              await applyTextureCallback(prevPattern, prevValue);
              await waitForEventOnce(UnityEvent.ReceiveDisplacementMapChanged);
              await getDataCallback(false);
              updateUICallback?.(prevPattern, false);
            } else {
              // If no previous pattern, remove displacement from Unity
              removeDisplacement();
              await waitForEventOnce(UnityEvent.ReceiveDisplacementMapChanged);
              await getDataCallback(false);
              updateUICallback?.(null, false);
            }
          },
          redo: async () => {
            if (nextPattern?.id) {
              await selectObjectBeforeUndoRedo(selectedObjectName) 
              await applyTextureCallback(nextPattern, prevValue);
              await waitForEventOnce(UnityEvent.ReceiveDisplacementMapChanged);
              await getDataCallback(false);
              updateUICallback?.(nextPattern, false);
            } else {
              // If no next pattern, remove displacement from Unity
              removeDisplacement();
              await waitForEventOnce(UnityEvent.ReceiveDisplacementMapChanged);
              await getDataCallback(false);
              updateUICallback?.(null, false);
            }
          },
        };
      },
      { pause: true, name: 'WALL_PATTERN_CHANGE' }
    );

    // Lock state change
    this._pushLockChange = this.createAction(
      ({ prevLock, nextLock, updateCallback }) => {
        if (prevLock === nextLock) return false;

        return {
          __ACTION__: 'WALL_LOCK_CHANGE',
          undo: () => {
            updateCallback?.(prevLock);
          },
          redo: () => {
            updateCallback?.(nextLock);
          },
        };
      },
      { pause: true, name: 'WALL_LOCK_CHANGE' }
    );

    // Button add action
    this._pushButtonAdd = this.createAction(
      ({ addedConfig, addedFilterTuftedButtonComponent, componentType, refreshCallback }) => {
        return {
          __ACTION__: 'BUTTON_ADD',
          undo: async () => {
            const disablePayload = {
              productBuilderModel: {
                operationType: 'Delete',
                tuftedButtonConfiguration: { ...addedConfig },
              },
            };
            disableGameObject(disablePayload);
            
            await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);
            // Refresh after operation
            refreshCallback?.()
            await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);
          },
          redo: async () => {
            const enablePayload = {
              productBuilderModel: {
                operationType: 'Delete',
                tuftedButtonConfiguration: { ...addedConfig },
              },
              propInfoCommandDatas: addedFilterTuftedButtonComponent?.map(({ objectName }) => ({
                objectName: objectName,
              })),
            };  
            enableGameObject(enablePayload);
            await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);
            
            // Refresh after operation
            refreshCallback?.()
            await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);
          },
        };
      },
      { pause: true, name: 'BUTTON_ADD' }
    );
  }

  _applyUnityChange = async (unityProp, value) => {
    // Width/Height (displacement)
    if (unityProp === 'displacement') {
      if (this.undoRedo.unityRedux?.changeSize) {
        const payload = {
          width: value.width,
          height: value.height,
          tilingOptionType: TILING_TYPE.DISPLACEMENT,
          count: 3,
        };
        this.undoRedo.unityRedux.changeSize(payload);
      }
    }
    // Intensity (depth)
    else if (unityProp === 'displacementIntensity') {
      changeIntensity(value);
    }
    // Position X/Y
    else if (unityProp === 'positionX' || unityProp === 'positionY') {
      if (this.undoRedo.unityRedux?.changePosition) {
        const axis = unityProp === 'positionX' ? 'x' : 'y';
        const otherAxis = axis === 'x' ? 'y' : 'x';
        const otherProp = otherAxis === 'x' ? 'positionX' : 'positionY';

        const payload = {
          x: axis === 'x' ? value : this._getTempValue(otherProp),
          y: axis === 'y' ? value : this._getTempValue(otherProp),
          tilingOptionType: TILING_TYPE.DISPLACEMENT,
          count: 3,
        };
        this.undoRedo.unityRedux.changePosition(payload);
      }
    }
    // Rotation
    else if (unityProp === 'rotation') {
      if (this.undoRedo.unityRedux?.changeRotation) {
        const payload = {
          width: this._getTempValue('width'),
          height: this._getTempValue('height'),
          x: this._getTempValue('positionX'),
          y: this._getTempValue('positionY'),
          tilingOptionType: TILING_TYPE.DISPLACEMENT,
          rotation: value,
        };
        // this.undoRedo.unityRedux.changeRotation(payload);
      }
    }
  };

  _getTempValue = (propName) => {
    const mapping = {
      width: this._tempWidth,
      height: this._tempHeight,
      positionX: this._tempPositionX,
      positionY: this._tempPositionY,
    };
    return mapping[propName] || 0;
  };

  _wrapUpdateCallback = (originalCallback) => {
    return (unityProp, value) => {
      // Store temp values for position/rotation
      if (unityProp === 'displacement' && typeof value === 'object') {
        this._tempWidth = value.width;
        this._tempHeight = value.height;
      } else if (unityProp === 'positionX') {
        this._tempPositionX = value;
      } else if (unityProp === 'positionY') {
        this._tempPositionY = value;
      }

      originalCallback?.(unityProp, value);
    };
  };

  // For UndoRedoLockedInput (Width/Height)
  changeSize = ({ name, prevValue, nextValue, data }) => {
    const { updateCallback } = data;

    // Convert to CM for Unity
    const convertValue = (val) => {
      if (typeof val === 'object') {
        return {
          width: toUnityFixed(convertActiveUnitToCM(val.width), 3),
          height: toUnityFixed(convertActiveUnitToCM(val.height), 3),
        };
      }
      return toUnityFixed(convertActiveUnitToCM(val), 3);
    };

    const prev = convertValue(prevValue);
    const next = convertValue(nextValue);

    this._pushSingleProp({
      prevValue: prev,
      nextValue: next,
      unityProp: 'displacement',
      updateCallback: this._wrapUpdateCallback(updateCallback),
    });
     undoRedoObject.pause();
  };

  // Start tracking a property change
  startPropertyChange({ unityProp, initialValue }) {
    undoRedoObject.resume();
    const key = `wall__${unityProp}`;
    const normalizedValue =
      typeof initialValue === 'object'
        ? { width: toUnityFixed(initialValue.width, 3), height: toUnityFixed(initialValue.height, 3) }
        : toUnityFixed(initialValue, 3);

    this.pending.set(key, {
      unityProp,
      initialValue: normalizedValue,
      lastValue: normalizedValue,
    });
    undoRedoObject.pause();
  }

  // Update pending change
  updatePendingChange({ unityProp, currentValue }) {
    const key = `wall__${unityProp}`;
    const item = this.pending.get(key);
    if (!item) return;

    const normalizedValue =
      typeof currentValue === 'object'
        ? { width: toUnityFixed(currentValue.width, 3), height: toUnityFixed(currentValue.height, 3) }
        : toUnityFixed(currentValue, 3);

    item.lastValue = normalizedValue;
  }

  // End tracking and create undo/redo action
  endPropertyChange({ unityProp, finalValue, updateCallback }) {
    undoRedoObject.resume();
    const key = `wall__${unityProp}`;
    const item = this.pending.get(key);
    if (!item) return;

    const prevValue = typeof item.initialValue === 'object' ? { ...item.initialValue } : item.initialValue;
    const nextValue = typeof item.lastValue === 'object' ? { ...item.lastValue } : item.lastValue;

    this._pushSingleProp({
      prevValue,
      nextValue,
      unityProp,
      updateCallback: this._wrapUpdateCallback(updateCallback),
    });

    this.pending.delete(key);
    undoRedoObject.pause();
  }

  // Cancel pending change
  cancelPropertyChange({ unityProp }) {
    const key = `wall__${unityProp}`;
    this.pending.delete(key);
  }

  // Clear all pending changes
  clearAllPendingChanges() {
    this.pending.clear();
  }

  // Pattern selection
  changePattern = async ({ prevPattern, nextPattern, applyTextureCallback, getDataCallback, updateUICallback, wasListOpen, prevValue }) => {
    undoRedoObject.resume();
    
    this._pushPatternChange({
      prevPattern,
      nextPattern,
      applyTextureCallback,
      getDataCallback,
      updateUICallback,
      wasListOpen,
      prevValue,
    });
    
    undoRedoObject.pause();
  };

  // Lock toggle
  changeLockState = ({ prevLock, nextLock, updateCallback }) => {
    undoRedoObject.resume();
    
    this._pushLockChange({
      prevLock,
      nextLock,
      updateCallback,
    });
    
    undoRedoObject.pause();
  };

  // Button add operation
  addButtonAddAction = ({ addedConfig, addedFilterTuftedButtonComponent, componentType, refreshCallback }) => {
    undoRedoObject.resume();
    
    this._pushButtonAdd({
      addedConfig,
      addedFilterTuftedButtonComponent,
      componentType,
      refreshCallback,
    });
    
    undoRedoObject.pause();
  };
}
